import { BASE_GRID_OPTIONS, PoGrid } from '@/components/grid/PoGrid';
import { FunctionComponent } from 'react';
import { useNavigate } from 'react-router-dom';
import { GridApi, GridOptions, ICellRendererParams, RowDoubleClickedEvent } from 'ag-grid-community';
import LinkCellRenderer, { LinkCellRendererParams } from '@/components/grid/cells/LinkCellRenderer';
import IconButtonCellRenderer, { IIconButtonCellRendererParams } from '@/components/grid/cells/IconButtonCellRenderer';
import ErrorLoadingDataAlert from '@/components/feedback/ErrorLoadingDataAlert';
import { ColDefOrGroup } from '@/lib/ag-grid/types';
import { AssetTypeTotalModel } from '@/modules/asset-types';
import { DisplayMode } from '@/types/display-mode.enum';
import { useTranslation } from '@/lib';
import { AccountTree } from '@mui/icons-material';

interface LocationBalanceGridProps {
  data?: AssetTypeTotalModel[];
  isError: boolean;
  noBorder?: boolean;
  isLoading?: boolean;
  balanceDisplayMode?: DisplayMode;
  locationId: number;
  onBuildupClick?: (assetType: AssetTypeTotalModel) => void;
}

export const LocationBalanceGrid: FunctionComponent<LocationBalanceGridProps> = ({
  data,
  isError,
  noBorder,
  isLoading,
  balanceDisplayMode,
  locationId,
  onBuildupClick,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const customGridOptions: GridOptions<AssetTypeTotalModel> = {
    ...BASE_GRID_OPTIONS,

    onRowDoubleClicked(event: RowDoubleClickedEvent<AssetTypeTotalModel, unknown>) {
      navigate(`/app/configuration/assets/types/${event.data?.id}`);
    },
  };

  const columns: ColDefOrGroup<AssetTypeTotalModel>[] = [
    {
      field: 'code',
      headerName: t('asset_type_code'),
    },
    {
      field: 'name',
      headerName: t('asset_type_name'),
      cellRenderer: LinkCellRenderer,
      cellRendererParams: (params: ICellRendererParams<AssetTypeTotalModel>): LinkCellRendererParams => ({
        pathname: params.data?.id ? `/app/configuration/assets/types/${params.data?.id}` : undefined,
      }),
    },
  ];

  if (balanceDisplayMode === DisplayMode.HIERARCHICAL) {
    columns.push({
      field: 'hierarchicalBalance',
      headerName: t('balance'),
      cellRenderer: LinkCellRenderer,
      cellRendererParams: (params: ICellRendererParams<AssetTypeTotalModel>): LinkCellRendererParams => ({
        pathname: params.data?.id ? `/app/locations/${locationId}/assets` : undefined,
        search: params.data?.id ? `assetTypeIds=${params.data?.id}` : undefined,
      }),
    });
  } else {
    columns.push({
      field: 'individualBalance',
      headerName: t('balance'),
      cellRenderer: LinkCellRenderer,
      cellRendererParams: (params: ICellRendererParams<AssetTypeTotalModel>): LinkCellRendererParams => ({
        pathname: params.data?.id ? `/app/locations/${locationId}/assets` : undefined,
        search: params.data?.id ? `assetTypeIds=${params.data?.id}` : undefined,
      }),
    });
  }

  // Add Buildup column
  columns.push({
    headerName: t('buildup'),
    colId: 'buildup',
    width: 100,
    cellRenderer: IconButtonCellRenderer,
    cellRendererParams: (params: ICellRendererParams<AssetTypeTotalModel>): IIconButtonCellRendererParams<AssetTypeTotalModel> => ({
      icon: <AccountTree fontSize="small" />,
      callback: (rowData: AssetTypeTotalModel) => {
        if (onBuildupClick) {
          onBuildupClick(rowData);
        }
      },
      tooltip: t('view_buildup'),
    }),
  });

  return (
    <div className="flex h-full flex-1 flex-grow flex-col">
      {isError ? (
        <ErrorLoadingDataAlert />
      ) : (
        <PoGrid
          noBorder={noBorder}
          isLoading={isLoading}
          colDefs={columns}
          rowData={data}
          gridOptions={customGridOptions}
          disableDefaultGridOptions
        />
      )}
    </div>
  );
};
